/* eslint-disable no-empty */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import imageCompression from "browser-image-compression";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { S } from "@/pages/ibloom/styles";
import ClaimAccountCard from "@/pages/ibloom/ClaimAccountCard";

const SCAN_MSGS = [
  "🔍 MunimJi is looking at your shelf…",
  "📦 Finding every product on the shelf…",
  "✂️ Cropping each product photo…",
  "✍️ Writing names & descriptions…",
];

const CATS = { healthcare: "🩺", fashion: "👗", beauty: "💄", baby: "🍼", pharmacy: "💊", electronics: "🔌", home: "🏠", stationery: "✏️", other: "🛍️" };

export default function ShelfScan() {
  const navigate = useNavigate();
  const { user, loading: authLoading, loginAsGuest } = useAuth();
  const guestTriedRef = useRef(false);
  const autoCaptureRef = useRef(false);
  const fileRef = useRef(null);
  const replaceRef = useRef(null);
  const replaceTargetRef = useRef(null);
  const [phase, setPhase] = useState("capture"); // capture | scanning | review | publishing | done
  const [shelfPreview, setShelfPreview] = useState(null);
  const [msgIdx, setMsgIdx] = useState(0);
  const [drafts, setDrafts] = useState([]);
  const [me, setMe] = useState(null);
  const [whatsapp, setWhatsapp] = useState("");
  const [geo, setGeo] = useState(null);
  const [pubProgress, setPubProgress] = useState({ done: 0, total: 0 });
  const [published, setPublished] = useState([]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      // No signup needed — silently create a unique guest account and continue.
      if (!guestTriedRef.current) {
        guestTriedRef.current = true;
        loginAsGuest().catch(() => toast("Couldn't start your session — check internet and retry."));
      }
      return;
    }
    api.get("/shopkeeper/me").then((r) => setMe(r.data)).catch(() => {});
    // Arrived from the intro carousel (?capture=1) → open the device camera right away.
    if (!autoCaptureRef.current && new URLSearchParams(window.location.search).get("capture") === "1") {
      autoCaptureRef.current = true;
      navigate("/shelf-scan", { replace: true });
      setTimeout(() => fileRef.current?.click(), 350);
    }
    navigator.geolocation?.getCurrentPosition(
      (p) => setGeo({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => {},
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (phase !== "scanning") return;
    const t = setInterval(() => setMsgIdx((i) => (i + 1) % SCAN_MSGS.length), 3500);
    return () => clearInterval(t);
  }, [phase]);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setPhase("scanning");
    setMsgIdx(0);
    try {
      const compressed = await imageCompression(f, { maxSizeMB: 1.2, maxWidthOrHeight: 1600, initialQuality: 0.82, useWebWorker: true });
      const dataUrl = await imageCompression.getDataUrlFromFile(compressed);
      setShelfPreview(dataUrl);
      const r = await api.post("/shelf/scan", { image_data: dataUrl });
      const items = (r.data.items || []).map((it, i) => ({
        id: i,
        img: it.crop_image_data,
        name: it.name,
        category: it.category,
        price: it.price_guess != null ? String(it.price_guess) : "",
        mrp: "",
        pack: "",
        description: it.description,
        selected: true,
        status: "draft",
      }));
      if (!items.length) {
        toast("Couldn't spot any products. Try a closer, well-lit photo of the shelf.");
        setPhase("capture");
        return;
      }
      setDrafts(items);
      setPhase("review");
    } catch (err) {
      toast(err?.response?.data?.detail || "Shelf scan failed. Please try again.");
      setPhase("capture");
    }
  };

  const setDraft = (id, patch) => setDrafts((ds) => ds.map((d) => (d.id === id ? { ...d, ...patch } : d)));

  const pickReplacement = (id, mode = "replace") => {
    replaceTargetRef.current = { id, mode };
    replaceRef.current?.click();
  };

  const onReplacePick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    const target = replaceTargetRef.current;
    if (!f || target == null) return;
    try {
      const compressed = await imageCompression(f, { maxSizeMB: 0.5, maxWidthOrHeight: 1024, initialQuality: 0.8, useWebWorker: true });
      const dataUrl = await imageCompression.getDataUrlFromFile(compressed);
      if (target.mode === "add") {
        setDrafts((ds) => ds.map((d) => (d.id === target.id ? { ...d, extras: [...(d.extras || []), dataUrl].slice(0, 4) } : d)));
        toast("＋ Extra photo added to this product.");
      } else {
        setDraft(target.id, { img: dataUrl });
        toast("📷 Photo replaced for this product.");
      }
    } catch {
      toast("Couldn't read that photo — try again.");
    }
  };

  const selectedDrafts = drafts.filter((d) => d.selected);
  const missingPrice = selectedDrafts.some((d) => !d.price || Number(d.price) <= 0);
  const needsWhatsapp = me && !me.is_shopkeeper && !(me.whatsapp || "").trim();
  const waDigits = (whatsapp || "").replace(/\D/g, "");
  const waOk = !needsWhatsapp || (waDigits.length >= 10 && waDigits.length <= 15);

  const publishAll = async () => {
    if (missingPrice) { toast("Add a price (₹) on every selected item first."); return; }
    if (!waOk) { toast("Enter your WhatsApp number so buyers can reach you."); return; }
    setPhase("publishing");
    setPubProgress({ done: 0, total: selectedDrafts.length });
    const ok = [];
    for (const d of selectedDrafts) {
      try {
        const payload = {
          title: d.name.trim(),
          description: d.description || d.name,
          category: d.category,
          price: Number(d.price),
          original_image_data: d.img,
          ai_image_pending: true,
          image_style: "studio",
          ...(d.extras && d.extras.length ? { extra_images: d.extras } : {}),
          ...(Number(d.mrp) > Number(d.price) ? { mrp: Number(d.mrp) } : {}),
          ...(d.pack && d.pack.trim() ? { pack_size: d.pack.trim() } : {}),
          ...(geo ? { lat: geo.lat, lng: geo.lng } : {}),
          ...(needsWhatsapp ? { whatsapp: waDigits } : {}),
        };
        const r = await api.post("/products/publish", payload);
        ok.push(r.data);
        setDraft(d.id, { status: "live" });
      } catch (err) {
        setDraft(d.id, { status: "failed" });
        toast(`"${d.name}": ${err?.response?.data?.detail || "publish failed"}`);
      }
      setPubProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    setPublished(ok);
    setPhase("done");
  };

  const card = { background: "white", border: "1px solid #EAE8DC", borderRadius: 16, boxShadow: "0 3px 10px rgba(15,20,30,0.05)" };
  const inputSt = { width: "100%", padding: "8px 10px", borderRadius: 10, border: "1px solid #DDD8C8", fontSize: 13, background: "#FCFBF7", boxSizing: "border-box" };

  return (
    <div data-testid="shelf-scan-page" style={{ minHeight: "100dvh", background: "#FAF9F6" }}>
      <div style={{ position: "sticky", top: 0, zIndex: 20, background: "rgba(250,249,246,0.92)", backdropFilter: "blur(10px)", borderBottom: "1px solid #EAE8DC", padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
        <button data-testid="shelf-scan-back-btn" onClick={() => navigate(-1)} style={{ width: 34, height: 34, borderRadius: "50%", border: "1px solid #EAE8DC", background: "white", fontSize: 16, cursor: "pointer", color: "#0F3D2E", flexShrink: 0 }}>‹</button>
        <div style={{ ...S.fraunces, fontSize: 18, fontWeight: 800, color: "#0F3D2E" }} data-no-translate>Shelf Scan</div>
        <span style={{ fontSize: 10, fontWeight: 800, background: "#C97B3C22", color: "#C97B3C", borderRadius: 100, padding: "3px 9px", letterSpacing: 0.4 }} data-no-translate>NEW</span>
      </div>

      <div style={{ maxWidth: 560, margin: "0 auto", padding: "18px 16px 120px" }}>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPick} style={{ display: "none" }} data-testid="shelf-scan-file-input" />
        <input ref={replaceRef} type="file" accept="image/*" capture="environment" onChange={onReplacePick} style={{ display: "none" }} data-testid="shelf-scan-replace-input" />

        {phase === "capture" && (
          <>
            <div style={{ ...card, background: "linear-gradient(135deg, #0F3D2E, #1C6B4A)", color: "white", padding: "24px 20px", border: "none" }}>
              <h1 style={{ ...S.fraunces, fontSize: 23, fontWeight: 800, lineHeight: 1.25, margin: 0 }}>One photo. Whole shelf online.</h1>
              <p style={{ fontSize: 13, opacity: 0.9, lineHeight: 1.55, margin: "8px 0 0" }}>
                Snap your full shelf — MunimJi finds every product, cuts out each photo, writes the listing. You just confirm prices.
              </p>
              <button data-testid="shelf-scan-capture-btn" onClick={() => fileRef.current?.click()} style={{ marginTop: 18, width: "100%", padding: 15, borderRadius: 100, border: "none", background: "white", color: "#0F3D2E", fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 8px 22px rgba(0,0,0,0.18)" }}>
                📸 Snap my shelf
              </button>
            </div>
            <div style={{ ...card, marginTop: 14, padding: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: "#8A94A6", marginBottom: 8 }} data-no-translate>TIPS FOR A GOOD SCAN</div>
              {["Stand ~1 metre from the shelf", "Good light, labels facing the camera", "One shelf section per photo works best"].map((t, i) => (
                <div key={i} style={{ fontSize: 13, color: "#444", padding: "4px 0" }}>✓ {t}</div>
              ))}
            </div>
            <button data-testid="shelf-scan-sentry-link" onClick={() => navigate("/shelf-sentry")} style={{ ...card, marginTop: 14, width: "100%", padding: "15px 16px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left", background: "#0E1512", border: "1px solid #1F4A3A" }}>
              <span style={{ fontSize: 24 }}>🛰️</span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: "block", fontSize: 13.5, fontWeight: 800, color: "#9FE8C4" }}>Shelf Sentry — hands-free mode</span>
                <span style={{ display: "block", fontSize: 11.5, color: "#6B8578", marginTop: 2 }}>Mount a spare phone facing the shelf — it auto-scans every 15 min</span>
              </span>
              <span style={{ color: "#5EE39A", fontWeight: 800 }}>›</span>
            </button>
          </>
        )}

        {phase === "scanning" && (
          <div data-testid="shelf-scan-progress" style={{ ...card, padding: 22, textAlign: "center" }}>
            {shelfPreview && <img src={shelfPreview} alt="shelf" style={{ width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: 12, filter: "brightness(0.92)" }} />}
            <div style={{ marginTop: 16, height: 5, borderRadius: 100, background: "#EAE8DC", overflow: "hidden" }}>
              <div style={{ height: "100%", width: "45%", borderRadius: 100, background: "linear-gradient(90deg,#1C6B4A,#C97B3C)", animation: "shelfscan-slide 1.4s ease-in-out infinite alternate" }} />
            </div>
            <div style={{ marginTop: 14, fontSize: 14.5, fontWeight: 800, color: "#0F3D2E" }}>{SCAN_MSGS[msgIdx]}</div>
            <div style={{ fontSize: 12, color: "#888", marginTop: 5 }}>Usually 15–30 seconds</div>
            <style>{`@keyframes shelfscan-slide { from { margin-left: 0; } to { margin-left: 55%; } }`}</style>
          </div>
        )}

        {(phase === "review" || phase === "publishing") && (
          <>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ ...S.fraunces, fontSize: 18, fontWeight: 800, color: "#0F3D2E" }} data-testid="shelf-scan-found-count">
                Found {drafts.length} products
              </div>
              <button data-testid="shelf-scan-rescan-btn" onClick={() => { setDrafts([]); setPhase("capture"); }} disabled={phase === "publishing"} style={{ border: "none", background: "none", color: "#C97B3C", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>↺ Re-scan</button>
            </div>
            <div style={{ fontSize: 12, color: "#777", marginBottom: 12 }}>Fix names & prices, untick anything wrong, then publish.</div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {drafts.map((d) => (
                <div key={d.id} data-testid={`shelf-draft-${d.id}`} style={{ ...card, padding: 10, opacity: d.selected ? 1 : 0.45, position: "relative" }}>
                  <button
                    data-testid={`shelf-draft-toggle-${d.id}`}
                    onClick={() => phase !== "publishing" && setDraft(d.id, { selected: !d.selected })}
                    style={{ position: "absolute", top: 7, right: 7, zIndex: 2, width: 26, height: 26, borderRadius: "50%", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 900, background: d.selected ? "#1C6B4A" : "#CCC", color: "white" }}
                  >{d.selected ? "✓" : "＋"}</button>
                  {d.status === "live" && <span style={{ position: "absolute", top: 7, left: 7, zIndex: 2, fontSize: 9.5, fontWeight: 900, background: "#1C6B4A", color: "white", borderRadius: 100, padding: "3px 8px" }}>● LIVE</span>}
                  {d.status === "failed" && <span style={{ position: "absolute", top: 7, left: 7, zIndex: 2, fontSize: 9.5, fontWeight: 900, background: "#B3261E", color: "white", borderRadius: 100, padding: "3px 8px" }}>FAILED</span>}
                  <div style={{ position: "relative" }}>
                    <img src={d.img} alt={d.name} style={{ width: "100%", height: 110, objectFit: "contain", borderRadius: 10, background: "#F4F2EA" }} />
                    <button
                      data-testid={`shelf-draft-replace-${d.id}`}
                      onClick={() => phase !== "publishing" && pickReplacement(d.id, "replace")}
                      style={{ position: "absolute", right: 5, bottom: 5, border: "none", borderRadius: 100, background: "rgba(15,61,46,0.85)", color: "white", fontSize: 10, fontWeight: 800, padding: "4px 9px", cursor: "pointer", display: "flex", alignItems: "center", gap: 3 }}
                    >📷 Replace</button>
                    <button
                      data-testid={`shelf-draft-addphoto-${d.id}`}
                      onClick={() => phase !== "publishing" && pickReplacement(d.id, "add")}
                      style={{ position: "absolute", left: 5, bottom: 5, border: "none", borderRadius: 100, background: "rgba(201,123,60,0.9)", color: "white", fontSize: 10, fontWeight: 800, padding: "4px 9px", cursor: "pointer" }}
                    >＋{d.extras && d.extras.length ? ` ${d.extras.length}` : " Photo"}</button>
                  </div>
                  <div style={{ fontSize: 10, color: "#8A94A6", fontWeight: 800, marginTop: 7 }}>{CATS[d.category] || "🛍️"} {d.category}</div>
                  <input
                    data-testid={`shelf-draft-name-${d.id}`}
                    value={d.name}
                    onChange={(e) => setDraft(d.id, { name: e.target.value })}
                    disabled={phase === "publishing"}
                    style={{ ...inputSt, marginTop: 5, fontWeight: 700 }}
                  />
                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: "#0F3D2E" }}>₹</span>
                    <input
                      data-testid={`shelf-draft-price-${d.id}`}
                      type="number"
                      min="1"
                      placeholder="Price"
                      value={d.price}
                      onChange={(e) => setDraft(d.id, { price: e.target.value })}
                      disabled={phase === "publishing"}
                      style={{ ...inputSt, fontWeight: 800, borderColor: d.selected && (!d.price || Number(d.price) <= 0) ? "#C97B3C" : "#DDD8C8" }}
                    />
                  </div>
                  <div style={{ display: "flex", gap: 5, marginTop: 5 }}>
                    <input
                      data-testid={`shelf-draft-mrp-${d.id}`}
                      type="number"
                      min="0"
                      placeholder="MRP ₹"
                      title="Retail price (MRP) — shown struck-through"
                      value={d.mrp}
                      onChange={(e) => setDraft(d.id, { mrp: e.target.value })}
                      disabled={phase === "publishing"}
                      style={{ ...inputSt, fontSize: 11.5 }}
                    />
                    <input
                      data-testid={`shelf-draft-pack-${d.id}`}
                      placeholder="Size · 500ml / 10 tab"
                      value={d.pack}
                      onChange={(e) => setDraft(d.id, { pack: e.target.value })}
                      disabled={phase === "publishing"}
                      maxLength={60}
                      style={{ ...inputSt, fontSize: 11.5 }}
                    />
                  </div>
                  {Number(d.mrp) > Number(d.price) && Number(d.price) > 0 && (
                    <div data-testid={`shelf-draft-off-${d.id}`} style={{ marginTop: 4, fontSize: 10, fontWeight: 800, color: "#1C6B4A" }}>
                      ✓ {Math.round((1 - Number(d.price) / Number(d.mrp)) * 100)}% off will be shown to buyers
                    </div>
                  )}
                  <textarea
                    data-testid={`shelf-draft-desc-${d.id}`}
                    value={d.description}
                    onChange={(e) => setDraft(d.id, { description: e.target.value })}
                    disabled={phase === "publishing"}
                    placeholder="Add product details — size, brand, condition…"
                    rows={2}
                    style={{ ...inputSt, marginTop: 6, resize: "vertical", fontSize: 11.5, lineHeight: 1.4, fontFamily: "inherit", color: "#555" }}
                  />
                </div>
              ))}
            </div>

            {needsWhatsapp && (
              <div style={{ ...card, marginTop: 14, padding: 14 }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: "#0F3D2E" }}>📱 Your WhatsApp number (first-time setup)</div>
                <div style={{ fontSize: 11.5, color: "#777", margin: "3px 0 8px" }}>Buyers will contact you here. Creates your free Mapbazar shop.</div>
                <input data-testid="shelf-scan-whatsapp-input" type="tel" placeholder="10-digit mobile" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} style={inputSt} />
              </div>
            )}

            <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, padding: "12px 16px calc(12px + env(safe-area-inset-bottom))", background: "rgba(250,249,246,0.95)", backdropFilter: "blur(10px)", borderTop: "1px solid #EAE8DC" }}>
              <div style={{ maxWidth: 560, margin: "0 auto" }}>
                <button
                  data-testid="shelf-scan-publish-btn"
                  onClick={publishAll}
                  disabled={phase === "publishing" || selectedDrafts.length === 0 || missingPrice || !waOk}
                  style={{ width: "100%", padding: 15, borderRadius: 100, border: "none", fontSize: 15, fontWeight: 800, cursor: "pointer", color: "white", background: phase === "publishing" || selectedDrafts.length === 0 || missingPrice || !waOk ? "#B9B4A5" : "linear-gradient(135deg, #C97B3C, #8B1A4A)", boxShadow: "0 10px 28px rgba(139,26,74,0.3)" }}
                >
                  {phase === "publishing"
                    ? `Publishing ${pubProgress.done}/${pubProgress.total}…`
                    : missingPrice
                      ? "Add ₹ price on selected items"
                      : `🚀 Publish ${selectedDrafts.length} products live`}
                </button>
              </div>
            </div>
          </>
        )}

        {phase === "done" && (
          <>
          <div data-testid="shelf-scan-done" style={{ ...card, padding: 24, textAlign: "center" }}>
            <div style={{ fontSize: 44 }}>🎉</div>
            <div style={{ ...S.fraunces, fontSize: 21, fontWeight: 800, color: "#0F3D2E", marginTop: 8 }}>
              {published.length} products are live on Mapbazar!
            </div>
            <p style={{ fontSize: 13, color: "#666", lineHeight: 1.55, marginTop: 8 }}>
              Your shelf is now a shoppable online store. Nearby customers can find and buy these items on the map.
            </p>
            <div style={{ marginTop: 10, fontSize: 12, color: "#1C6B4A", fontWeight: 700, background: "#1C6B4A12", borderRadius: 12, padding: "8px 12px" }}>
              ✨ Studio photos are baking — each listing gets an Amazon-style white-background photo automatically in ~1 minute.
            </div>
            <button data-testid="shelf-scan-view-map-btn" onClick={() => navigate("/")} style={{ marginTop: 16, width: "100%", padding: 14, borderRadius: 100, border: "none", background: "#0F3D2E", color: "white", fontSize: 14.5, fontWeight: 800, cursor: "pointer" }}>
              🗺️ See them on the map
            </button>
            <button data-testid="shelf-scan-again-btn" onClick={() => { setDrafts([]); setPublished([]); setPhase("capture"); }} style={{ marginTop: 10, width: "100%", padding: 13, borderRadius: 100, border: "1px solid #EAE8DC", background: "white", color: "#0F3D2E", fontSize: 14, fontWeight: 800, cursor: "pointer" }}>
              📸 Scan another shelf
            </button>
          </div>
          <ClaimAccountCard defaultMobile={waDigits || me?.whatsapp || ""} />
          </>
        )}
      </div>
    </div>
  );
}
